import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Rocket,
  Building2,
  User,
  ChevronDown,
  ChevronRight,
  Calendar,
  FileText,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';
import { useDeliveryOverview, type DeliveryProject } from '@/hooks/useDelivery';
import type { RagStatus } from '@/hooks/useProjects';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

export function Delivery() {
  const { data, isLoading } = useDeliveryOverview();
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card p-8 text-center text-gray-500">
        Failed to load delivery overview
      </div>
    );
  }

  const { customers, summary } = data;

  const toggleCustomer = (id: string) => {
    setExpandedCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedCustomers(new Set(customers.map((c) => c.id)));
  };

  const collapseAll = () => {
    setExpandedCustomers(new Set());
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-xtravirt-dark">
            Delivery
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {summary.totalProjects} active project{summary.totalProjects !== 1 ? 's' : ''} across {summary.totalCustomers} customer{summary.totalCustomers !== 1 ? 's' : ''}
          </p>
        </div>
        {customers.length > 0 && (
          <div className="flex items-center gap-2">
            <button onClick={expandAll} className="btn-secondary text-sm">
              Expand All
            </button>
            <button onClick={collapseAll} className="btn-secondary text-sm">
              Collapse All
            </button>
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <SummaryCard
          icon={Rocket}
          label="Active Projects"
          value={summary.totalProjects.toString()}
          iconColor="text-xtravirt-cyan"
        />
        <SummaryCard
          icon={Building2}
          label="Customers"
          value={summary.totalCustomers.toString()}
          iconColor="text-xtravirt-navy"
        />
        <SummaryCard
          icon={TrendingUp}
          label="Total Value"
          value={formatCurrency(summary.totalValue)}
          iconColor="text-green-600"
        />
        <SummaryCard
          icon={Calendar}
          label="Total Days"
          value={`${(summary.totalDays / 100).toFixed(0)}`}
          iconColor="text-purple-600"
        />
      </div>

      {/* Health breakdown */}
      {summary.totalProjects > 0 && (
        <div className="card p-4 md:p-6">
          <h3 className="font-heading text-base font-semibold text-xtravirt-dark mb-3">
            Delivery Health
          </h3>
          <div className="grid grid-cols-4 gap-3">
            <HealthCell label="On Track" count={summary.byHealth.green} total={summary.totalProjects} color="green" />
            <HealthCell label="Attention" count={summary.byHealth.amber} total={summary.totalProjects} color="amber" />
            <HealthCell label="Critical" count={summary.byHealth.red} total={summary.totalProjects} color="red" />
            <HealthCell label="Not Set" count={summary.byHealth.na} total={summary.totalProjects} color="gray" />
          </div>
        </div>
      )}

      {/* Customer groups */}
      {customers.length === 0 ? (
        <div className="card p-8 text-center">
          <Rocket className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <h3 className="font-medium text-gray-900 mb-1">No projects in delivery</h3>
          <p className="text-sm text-gray-500">
            Projects appear here when their scoping stage is set to "In Delivery".
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {customers.map((customer) => {
            const isExpanded = expandedCustomers.has(customer.id);
            return (
              <div key={customer.id} className="card overflow-hidden !p-0">
                {/* Customer header */}
                <button
                  onClick={() => toggleCustomer(customer.id)}
                  className="w-full flex items-center justify-between gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                    <Building2 className="h-5 w-5 text-xtravirt-navy flex-shrink-0" />
                    <div className="min-w-0">
                      {customer.id !== 'unknown' ? (
                        <Link
                          to={`/customers/${customer.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-heading font-semibold text-xtravirt-dark hover:text-xtravirt-cyan transition-colors"
                        >
                          {customer.name}
                        </Link>
                      ) : (
                        <span className="font-heading font-semibold text-xtravirt-dark">
                          {customer.name}
                        </span>
                      )}
                      <p className="text-xs text-gray-500 mt-0.5">
                        {customer.projectCount} project{customer.projectCount !== 1 ? 's' : ''} &middot; {(customer.totalDays / 100).toFixed(0)} days
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-semibold text-green-700">
                      {formatCurrency(customer.totalValue, customer.projects[0]?.currency)}
                    </p>
                  </div>
                </button>

                {/* Projects list */}
                {isExpanded && (
                  <div className="border-t border-gray-100 divide-y divide-gray-100">
                    {customer.projects.map((project) => (
                      <DeliveryProjectRow key={project.id} project={project} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ Summary Card ============

interface SummaryCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  iconColor: string;
}

function SummaryCard({ icon: Icon, label, value, iconColor }: SummaryCardProps) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <Icon className={cn('h-8 w-8 flex-shrink-0', iconColor)} />
        <div className="min-w-0">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</p>
          <p className="text-xl md:text-2xl font-heading font-bold text-xtravirt-dark mt-0.5 truncate">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

// ============ Health Cell ============

interface HealthCellProps {
  label: string;
  count: number;
  total: number;
  color: 'green' | 'amber' | 'red' | 'gray';
}

function HealthCell({ label, count, total, color }: HealthCellProps) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const colorMap = {
    green: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    red: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    gray: { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' },
  };
  const cfg = colorMap[color];

  return (
    <div className={cn('rounded-lg p-3 text-center', cfg.bg)}>
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <span className={cn('h-2 w-2 rounded-full', cfg.dot)} />
        <span className={cn('text-xs font-medium', cfg.text)}>{label}</span>
      </div>
      <p className={cn('text-2xl font-heading font-bold', cfg.text)}>{count}</p>
      <p className="text-xs text-gray-500 mt-0.5">{pct}%</p>
    </div>
  );
}

// ============ Delivery Project Row ============

interface DeliveryProjectRowProps {
  project: DeliveryProject;
}

function DeliveryProjectRow({ project }: DeliveryProjectRowProps) {
  const healthDot = getHealthDot(project.healthStatus);

  return (
    <Link
      to={`/projects/${project.id}`}
      className="flex items-center justify-between gap-3 p-4 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <FileText className="h-4 w-4 text-xtravirt-cyan flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-xtravirt-dark truncate">
              {project.name}
            </span>
            <span className={cn('inline-block h-2 w-2 rounded-full flex-shrink-0', healthDot)} title={project.healthStatus} />
            {project.healthStatus === 'red' && project.healthNotes && (
              <span className="flex items-center gap-1 text-xs text-red-600" title={project.healthNotes}>
                <AlertTriangle className="h-3 w-3" />
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
            {project.opportunityTitle && (
              <span className="truncate max-w-[200px]">{project.opportunityTitle}</span>
            )}
            {project.engagementStartDate && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(project.engagementStartDate).toLocaleDateString()}
              </span>
            )}
            {project.deliveryLeadName && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {project.deliveryLeadName}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="font-semibold text-green-700 text-sm">
          {formatCurrency(project.totalCost, project.currency)}
        </p>
        <p className="text-xs text-gray-500">
          {(project.totalAdjustedDays / 100).toFixed(0)} days
        </p>
      </div>
    </Link>
  );
}

function getHealthDot(status: RagStatus): string {
  switch (status) {
    case 'green': return 'bg-green-500';
    case 'amber': return 'bg-amber-500';
    case 'red': return 'bg-red-500';
    default: return 'bg-gray-300';
  }
}

function formatCurrency(cents: number, currency: 'GBP' | 'USD' | 'EUR' = 'USD'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
